/**
 * Arquero-based unified data processor with data-driven operation dispatch.
 *
 * Architecture principles:
 * 1. Schema enums -> lookup tables -> Arquero ops (no switch statements for operation dispatch)
 * 2. Operation mappings are const data structures, not branching logic
 * 3. Single generic function per operation type, parameterized by the mapping
 *
 * This replaces scattered manual implementations with a single Arquero-powered processor.
 */
import type { ColumnTable } from 'arquero';
import * as aq from 'arquero';
import { op } from 'arquero';

import { inferColumnSchemas } from '#core/lib/transforms/column-schema.ts';
import { getNestedValue } from '#core/lib/utils/object.ts';

import { datasetToTable } from './dataset-to-table.ts';
import { evaluateDerivedFields } from './derived-fields.ts';
import { expandTimeseries } from './expand-timeseries.ts';
import { isSyntheticDeriveName } from './inline-expressions.ts';
import type { Action, AggregateOp, AggregationSpec, PipelineSpec, SortKey } from './pipeline-schema.ts';
import { prepareTableForAction } from './prepare-action.ts';
import type { PrefetchCtx } from './timeseries-prefetch.ts';
import type { DeriveSpec } from './types.ts';
import { createDataset } from '#plugin-chat/lib/data-pipeline/dataset.ts';
import { evaluateFilterTree } from '#plugin-chat/lib/data-pipeline/filter/evaluate.ts';
import type { ResolvedRootFilter } from '#plugin-chat/lib/data-pipeline/filter/resolve-source-refs.ts';
import type {
  AggregationResult,
  ColumnSchema,
  Dataset,
  DatasetColumnType,
  DatasetEnvelope,
  GroupedAggregations,
} from '#plugin-chat/lib/data-pipeline/types.ts';
import { isDatapointArraySamples } from '#plugin-chat/lib/timeseries/datapoint-detection.ts';
import {
  AmbiguousRowsError,
  ColumnNotFoundError,
  DatapointArrayShapeError,
  InvalidSpecError,
  ToolExecutionError,
} from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * Successful processing result.
 *
 * The processed rows and their column schema live inside {@link dataset}
 * (inherited from {@link DatasetEnvelope}), so callers get both the typed
 * column metadata and the row records from a single canonical shape.
 * Auxiliary outputs (global and grouped aggregations, pipeline warnings)
 * stay as sibling fields because they are not themselves datasets.
 */
interface TransformDatasetSuccess extends DatasetEnvelope {
  success: true;
  warnings?: string[];
  aggregations?: AggregationResult;
  groupedAggregations?: GroupedAggregations;
}

/** Result of {@link transformDataset}. Failures throw — there is no failure variant on the wire. */
export type TransformDatasetResult = TransformDatasetSuccess;

/** Maximum unique values allowed in pivot key column */
const MAX_PIVOT_CARDINALITY = 100;

/** Row count threshold for large dataset warning */
const MAX_ROWS_WARNING = 50000;

/** Operations that Arquero's rollup can handle natively */
const ARQUERO_ROLLUP_OPS: Record<string, (col: string) => unknown> = {
  count: () => op.count(),
  sum: (col) => op.sum(col),
  min: (col) => op.min(col),
  max: (col) => op.max(col),
  avg: (col) => op.mean(col), // Arquero uses 'mean' for average
};

/** Operations that need manual computation (window functions not allowed in rollup) */
const MANUAL_AGG_OPS = new Set(['first', 'last']);

// Use Arquero's internal ExprObject type for proper rollup typing
type TableExpr = ((d: Record<string, unknown>) => unknown) | string;
type ExprObject = Record<string, TableExpr>;

/**
 * Computes manual aggregations (first/last) that can't use Arquero rollup.
 * Uses lookup map pattern - no switch statements.
 */
type ManualAggFn = (data: Record<string, unknown>[], column?: string) => string | number | null;

const MANUAL_AGG_MAP: Record<string, ManualAggFn> = {
  first: (data, column) => {
    if (data.length === 0 || !column) {
      return null;
    }
    return data[0]?.[column] as string | number | null;
  },
  last: (data, column) => {
    if (data.length === 0 || !column) {
      return null;
    }
    return data.at(-1)?.[column] as string | number | null;
  },
};

/**
 * The value an aggregation op reports when its input is empty or entirely
 * null. Count-style ops report 0 (no rows seen); everything else reports
 * null so the LLM-facing layer can render "no data" instead of
 * misinterpreting 0 as a real measurement.
 */
function emptyAggregationValue(op: AggregationSpec['op']): number | null {
  return op === 'count' ? 0 : null;
}

/**
 * Creates default aggregation results for empty datasets.
 * Returns 0 for count operations, null for all others.
 */
function createEmptyAggregations(specs: AggregationSpec[]): AggregationResult {
  return Object.fromEntries(specs.map((spec) => [spec.name, emptyAggregationValue(spec.op)]));
}

/**
 * Computes aggregations on data, splitting between Arquero rollup and manual computation.
 * NO switch statement - uses lookup maps.
 *
 * Handles empty tables and all-null columns gracefully by returning default
 * values (0 for count, null for others). Arquero's rollup returns `undefined`
 * (or `NaN` for `mean`) when every input value is null — we normalize both
 * to the same shape as `createEmptyAggregations` so the LLM-facing layer can
 * render "no data" instead of misinterpreting `undefined`/`NaN` as 0.
 */
function computeAggregations(table: ColumnTable, specs: AggregationSpec[]): AggregationResult {
  // Handle empty table case - Arquero throws "Invalid column reference" when rolling up on empty tables
  // because aq.from([]) creates a table with NO columns
  if (table.numRows() === 0) {
    return createEmptyAggregations(specs);
  }

  const data = table.objects() as Record<string, unknown>[];

  // Split specs into Arquero-compatible and manual
  const arqueroSpecs = specs.filter((s) => !MANUAL_AGG_OPS.has(s.op));
  const manualSpecs = specs.filter((s) => MANUAL_AGG_OPS.has(s.op));

  const arqueroResult: AggregationResult =
    arqueroSpecs.length > 0
      ? (table
          .rollup(
            Object.fromEntries(
              arqueroSpecs.map(({ name, op: opName, column }) => [
                name,
                ARQUERO_ROLLUP_OPS[opName]?.(column ?? '') ?? op.count(),
              ]),
            ) as ExprObject,
          )
          .object() as AggregationResult)
      : {};

  const manualResult: AggregationResult = Object.fromEntries(
    manualSpecs.flatMap((spec) => {
      const fn = MANUAL_AGG_MAP[spec.op];
      return fn ? [[spec.name, fn(data, spec.column)] as const] : [];
    }),
  );

  const combined: AggregationResult = { ...arqueroResult, ...manualResult };
  return Object.fromEntries(
    specs.map((spec) => {
      const value = combined[spec.name];
      const isMissing = value === undefined || (typeof value === 'number' && Number.isNaN(value));
      return [spec.name, isMissing ? emptyAggregationValue(spec.op) : value];
    }),
  );
}

/**
 * Translate a pivot `aggregation` enum value into the shape Arquero's
 * `pivot(on, values)` expects for the `values` argument.
 *
 * - `first` / `last` → bare column name, which Arquero internally
 *   parses into `any(col)` (pick any value per group). For the
 *   unambiguous `(id, key)` cells produced by a typical wide-format
 *   reshape these are all equivalent; `last` differs only when a
 *   `(id, key)` pair has multiple rows and row order carries meaning
 *   — callers that depend on exact last-row semantics should sort +
 *   deduplicate upstream.
 * - Everything else delegates to {@link ARQUERO_ROLLUP_OPS}, the same
 *   op map used by {@link computeAggregations} so pivot and rollup
 *   share a single source of truth for aggregation semantics.
 */
function buildPivotValues(valueColumn: string, aggregation: AggregateOp): string | Record<string, unknown> {
  if (aggregation === 'first' || aggregation === 'last') {
    return valueColumn;
  }
  const opFn = ARQUERO_ROLLUP_OPS[aggregation];
  return { [valueColumn]: opFn ? opFn(valueColumn) : op.count() };
}

/** Result from apply-derive step (not to be confused with DeriveResult from types.ts). */
interface ApplyDeriveResult {
  table: ColumnTable;
  warnings: string[];
}

/**
 * Applies derived field computations using math.js.
 * Imported from existing derived-fields module for expression evaluation.
 *
 * Async because `evaluateDerivedFields` runs a timeseries-datapoint
 * prefetch when a derive expression references one of the timeseries
 * fetcher functions (`datapoints`, `hourly`, `daily`, `weekly`,
 * `monthly`). When `ctx` is omitted (or no derive references a fetcher)
 * the await resolves synchronously after the in-process compile + scope
 * setup.
 */
async function applyDerive(table: ColumnTable, derives: DeriveSpec[], ctx?: PrefetchCtx): Promise<ApplyDeriveResult> {
  // Convert to objects, evaluate with math.js, convert back
  const data = table.objects() as Record<string, unknown>[];
  const result = await evaluateDerivedFields(data, derives, { ctx });

  return {
    table: aq.from(result.data),
    warnings: result.warnings,
  };
}

/**
 * Applies sorting to a table.
 */
function applySort(table: ColumnTable, keys: readonly SortKey[]): ColumnTable {
  if (keys.length === 0) {
    return table;
  }
  const orderArgs = keys.map((k) => (k.direction === 'desc' ? aq.desc(k.column) : k.column));
  return table.orderby(...orderArgs);
}

/**
 * Delimiter for composite group keys.
 * Uses ASCII Record Separator (0x1E) which cannot appear unescaped in JSON strings.
 * This prevents delimiter collision when column values contain special characters like "|".
 */
const GROUP_KEY_DELIMITER = '\x1e';

/**
 * Creates a composite key from row values for grouping.
 */
function computeGroupKey(row: Record<string, unknown>, columns: string[]): string {
  // Shared dot-notation resolution with filter/select (SSOT via getNestedValue).
  // Without this, groupBy on a nested column like `string.externalId` would
  // silently collapse every row into a single "undefined" group — the same
  // bug class that affected filter before the dot-notation fix.
  return columns
    .map((col) => {
      const value = getNestedValue(row, col);
      // JSON.stringify(undefined) returns the JS primitive undefined, not a
      // string — which produces an empty segment in the joined key. Coerce to
      // null so the round-trip through JSON.parse in parseGroupKey is safe.
      return JSON.stringify(value ?? null);
    })
    .join(GROUP_KEY_DELIMITER);
}

/**
 * Parses a composite group key back into column values.
 */
function parseGroupKey(key: string, columns: string[]): Record<string, unknown> {
  const values = key.split(GROUP_KEY_DELIMITER).map((v) => JSON.parse(v) as unknown);
  return Object.fromEntries(columns.map((col, i) => [col, values[i]]));
}

/**
 * Compute aggregations for a group of rows.
 * Shared logic extracted from column-based and filter-based group-by.
 */
function computeGroupAggregations(rows: Record<string, unknown>[], aggregations: AggregationSpec[]): AggregationResult {
  const groupTable = aq.from(rows);
  return computeAggregations(groupTable, aggregations);
}

/**
 * Configuration for building grouped result.
 */
interface GroupedResultConfig {
  /** Map of group keys to rows */
  groups: Map<string, Record<string, unknown>[]>;
  /** Aggregation specs to apply */
  aggregations: AggregationSpec[];
  /** Function to build group key metadata from group key string and rows */
  keyBuilder: (key: string, rows: Record<string, unknown>[]) => Record<string, unknown>;
  /** Whether to include rows in result */
  includeRows?: boolean;
}

/**
 * Build GroupedAggregations result from groups map.
 * Shared structure building extracted from both group-by implementations.
 */
function buildGroupedResult(config: GroupedResultConfig): GroupedAggregations {
  const { groups, aggregations, keyBuilder, includeRows = false } = config;
  const entries = Array.from(groups);

  return {
    groups: Object.fromEntries(entries.map(([key, rows]) => [key, computeGroupAggregations(rows, aggregations)])),
    groupKeys: Object.fromEntries(entries.map(([key, rows]) => [key, keyBuilder(key, rows)])),
    ...(includeRows ? { groupRows: Object.fromEntries(entries.map(([key, rows]) => [key, rows])) } : {}),
  };
}

/**
 * Computes column-based groupBy with aggregations.
 */
function computeColumnBasedGroupBy(
  data: Record<string, unknown>[],
  columns: string[],
  aggregations: AggregationSpec[],
): GroupedAggregations {
  // Group by composite key — reduce accumulates rows under each group
  // without reassigning any bindings.
  const groups = data.reduce<Map<string, Record<string, unknown>[]>>((acc, row) => {
    const key = computeGroupKey(row, columns);
    return acc.set(key, [...(acc.get(key) ?? []), row]);
  }, new Map());

  return buildGroupedResult({
    groups,
    aggregations,
    keyBuilder: (key) => parseGroupKey(key, columns),
  });
}

/**
 * Converts grouped aggregations back to a table format.
 */
function groupedToTable(grouped: GroupedAggregations): ColumnTable {
  const rows = Object.entries(grouped.groupKeys).map(([key, groupKey]) => ({
    ...groupKey,
    ...(grouped.groups[key] ?? {}),
  }));
  return aq.from(rows);
}

/**
 * Per-action step result. Each executor returns either a new table (with
 * optional warnings / aggregation envelopes) or a structured failure.
 */
type ActionStepSuccess = {
  success: true;
  table: ColumnTable;
  warnings?: readonly string[];
  aggregations?: AggregationResult;
  groupedAggregations?: GroupedAggregations;
};
type ActionStepResult = ActionStepSuccess;

type ActionExecutor<K extends Action['type']> = (
  action: Extract<Action, { type: K }>,
  table: ColumnTable,
  ctx: PrefetchCtx | undefined,
) => Promise<ActionStepResult>;
/**
 * Pre-flight checks for `spread` / `unroll` actions:
 *
 * 1. **Column missing** → throws {@link ColumnNotFoundError} with the available
 *    column list, so the LLM gets the canonical "wrong column name" error
 *    instead of an opaque Arquero crash.
 * 2. **Datapoint-array column** → throws {@link DatapointArrayShapeError}
 *    pointing the LLM at `expandTimeseries`. Datapoint arrays have a
 *    first-class reshape primitive that produces a chart-ready wide dataset;
 *    spread/unroll on them would silently corrupt the chart.
 * 3. **Scalar column** → throws {@link InvalidSpecError}. spread/unroll
 *    require an array-typed column; running them on a scalar produces
 *    undefined behavior in Arquero (the previous failure mode was a garbled
 *    `Invalid column reference` with a sliced fragment of the column name).
 *
 * Returns silently when the column exists and contains plain (non-datapoint)
 * array values — the action proceeds normally.
 */
function datapointArrayGuard(op: 'spread' | 'unroll', column: string, table: ColumnTable): void {
  const columnNames = table.columnNames();
  if (!columnNames.includes(column)) {
    throw new ColumnNotFoundError(op, [column], columnNames);
  }
  const values = table.array(column) as readonly unknown[];
  if (isDatapointArraySamples(values)) {
    throw new DatapointArrayShapeError(op, column, true);
  }
  // `unroll`/`spread` require array-shaped cells. If every non-null cell is a
  // scalar, fail fast with a clear message — without this the call falls into
  // Arquero with undefined behavior.
  const hasAnyArray = values.some((v) => Array.isArray(v));
  const hasAnyNonNull = values.some((v) => v !== null && v !== undefined);
  if (hasAnyNonNull && !hasAnyArray) {
    throw new InvalidSpecError(`${op}: column "${column}" is not an array — every cell is a scalar value.`, {
      suggestions: [
        `${op} requires an array-typed column (every cell is an array of values).`,
        'For scalar columns, use `derive` to compute new columns or `pivot` / `fold` to reshape.',
        'If this column should contain arrays, check the upstream producer — some pipelines collapse arrays during aggregation.',
      ],
      details: { operation: op, column, sampleValue: values.find((v) => v !== null && v !== undefined) ?? null },
    });
  }
}

/**
 * Per-action executors. Each entry handles one `action.type` and is keyed
 * such that the action argument is narrowed to the matching variant.
 *
 * Adding a new action type means adding one entry here — no other site in
 * the executor changes. The action loop in {@link transformDataset} reads
 * this table by `action.type` and runs the matched executor.
 *
 * Failures throw a {@link ToolExecutionError} subclass — never return a
 * structured failure object. The reduce loop in {@link transformDataset}
 * lets exceptions propagate to the step boundary.
 */
const ACTION_EXECUTORS: { [K in Action['type']]: ActionExecutor<K> } = {
  filter: async (action, table) => {
    // The action.where type is the LLM-facing RootFilter, but transformStep's
    // resolveActionSourceRefs runs before this executor and replaces it with
    // a ResolvedRootFilter at runtime. Cast at the boundary so the evaluator
    // sees the actually-resolved shape.
    const filterSpec = action.where as unknown as ResolvedRootFilter;
    return {
      success: true,
      table: table.filter(aq.escape((d: Record<string, unknown>) => evaluateFilterTree(d, filterSpec))),
    };
  },

  derive: async (action, table, ctx) => {
    const result = await applyDerive(table, action.derives, ctx);
    return { success: true, table: result.table, warnings: result.warnings };
  },

  sort: async (action, table) => {
    const knownColumns = table.columnNames();
    const knownColumnSet = new Set(knownColumns);
    const missing = action.keys.map((k) => k.column).filter((c) => !knownColumnSet.has(c));
    if (missing.length > 0) {
      throw new ColumnNotFoundError('sort', missing, knownColumns);
    }
    return { success: true, table: applySort(table, action.keys) };
  },

  slice: async (action, table) => {
    const offset = action.offset ?? 0;
    if (action.limit === undefined) {
      return { success: true, table: offset > 0 ? table.slice(offset) : table };
    }
    return { success: true, table: table.slice(offset, offset + action.limit) };
  },

  rollup: async (action, table) => {
    if (action.columns === undefined || action.columns.length === 0) {
      // Global rollup: materialise into a single-row table so downstream
      // actions see ordinary columns.
      const aggregations = computeAggregations(table, action.aggregations);
      const cols = Object.fromEntries(Object.entries(aggregations).map(([k, v]) => [k, [v]]));
      return { success: true, table: aq.table(cols), aggregations };
    }
    const tableData = table.objects() as Record<string, unknown>[];
    const grouped = computeColumnBasedGroupBy(tableData, [...action.columns], action.aggregations);
    return { success: true, table: groupedToTable(grouped), groupedAggregations: grouped };
  },

  pivot: async (action, table) => {
    const cardinality = table.groupby(action.keyColumn).count().numRows();
    if (cardinality > MAX_PIVOT_CARDINALITY) {
      throw new AmbiguousRowsError(
        'pivot',
        `Column "${action.keyColumn}" has ${cardinality} unique values. Max allowed: ${MAX_PIVOT_CARDINALITY}.`,
        action.keyColumn,
        cardinality,
      );
    }
    // Every column that isn't keyColumn or valueColumn becomes an id column,
    // preserved across the pivot — pandas-style `pivot_wider`. Without this,
    // long-format input collapses into a single row.
    const idColumns = table.columnNames().filter((c) => c !== action.keyColumn && c !== action.valueColumn);
    const grouped = idColumns.length > 0 ? table.groupby(idColumns) : table;
    // Arquero's typed signature only accepts `ExprList`, but the runtime
    // accepts the object-form `{ name: agg(col) }` for aggregation —
    // documented in arquero's pivot.js. Cast through unknown.
    const values = buildPivotValues(action.valueColumn, action.aggregation ?? 'first') as unknown as Parameters<
      typeof grouped.pivot
    >[1];
    return { success: true, table: grouped.pivot(action.keyColumn, values) };
  },

  fold: async (action, table) => {
    if (action.columns.length === 0) {
      throw new InvalidSpecError('fold requires at least one column to fold.', {
        suggestions: ['Provide one or more columns in the `columns` array.'],
      });
    }
    const keyName = action.keyName ?? 'key';
    const valueName = action.valueName ?? 'value';
    return { success: true, table: table.fold([...action.columns], { as: [keyName, valueName] }) };
  },

  spread: async (action, table) => {
    datapointArrayGuard('spread', action.column, table);
    const options: { limit?: number } = {};
    if (action.limit !== undefined) {
      options.limit = action.limit;
    }
    return { success: true, table: table.spread(action.column, options) };
  },

  unroll: async (action, table) => {
    datapointArrayGuard('unroll', action.column, table);
    const options: { limit?: number; index?: string } = {};
    if (action.limit !== undefined) {
      options.limit = action.limit;
    }
    if (action.indexName !== undefined) {
      options.index = action.indexName;
    }
    return { success: true, table: table.unroll(action.column, options) };
  },

  expandTimeseries: async (action, table) => {
    const rows = table.objects() as Record<string, unknown>[];
    const result = expandTimeseries(rows, table.columnNames(), {
      columns: [...action.columns],
      ...(action.splitBy === undefined ? {} : { splitBy: action.splitBy }),
      ...(action.align === undefined ? {} : { align: action.align }),
    });
    return { success: true, table: aq.from(result.rows) };
  },
};

/**
 * Run one action by dispatching through {@link ACTION_EXECUTORS}. The cast
 * routes the indexed executor's narrowed signature through the union form
 * so a single invocation site handles every variant.
 *
 * Defense-in-depth: any non-{@link ToolExecutionError} exception thrown
 * from inside an action executor (typically Arquero throwing on a malformed
 * column reference, a wrong-shape input, or an unsupported operation) is
 * translated into an {@link InvalidSpecError} carrying the action type and
 * the original message in `details`. Without this wrap the LLM sees an
 * `UnexpectedError` with `recovery: fatal` and a garbled fragment from
 * Arquero's internal error formatter (e.g. `"estamp"` from `"timestamp"`),
 * which is both unactionable and tells the LLM to give up. With the wrap
 * the LLM sees `recovery: retry-with-different-input` and the originating
 * action so it can correct course.
 *
 * Typed throws from the executor (`ColumnNotFoundError`,
 * `AmbiguousRowsError`, `DatapointArrayShapeError`, etc.) propagate
 * unchanged.
 */
async function runAction(action: Action, table: ColumnTable, ctx: PrefetchCtx | undefined): Promise<ActionStepResult> {
  const exec = ACTION_EXECUTORS[action.type] as (
    a: Action,
    t: ColumnTable,
    c: PrefetchCtx | undefined,
  ) => Promise<ActionStepResult>;
  try {
    return await exec(action, table, ctx);
  } catch (err) {
    if (err instanceof ToolExecutionError) {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    throw new InvalidSpecError(`${action.type} action failed: ${message}`, {
      suggestions: [
        `Verify the columns referenced by this ${action.type} action exist on the input dataset.`,
        'For unroll/spread, the referenced column must be array-typed.',
        'Column names must be bare identifiers — do not wrap them in extra quotes (e.g. write `myColumn`, not `"myColumn"`).',
        "Inspect the previous step's output schema before retrying.",
      ],
      details: { actionType: action.type, action },
    });
  }
}

/**
 * Processes a {@link Dataset} through an ordered pipeline of actions using
 * Arquero.
 *
 * Each action runs in the order the caller declared it. Per-action behavior
 * is delegated to {@link ACTION_EXECUTORS}; this function owns the orchestration
 * (per-action column-ref resolution, action dispatch, synthetic-column
 * stripping, schema propagation).
 *
 * Action executors throw {@link ToolExecutionError} subclasses on failure —
 * this function lets exceptions propagate to the step boundary unchanged.
 *
 * ## Column-reference resolution
 *
 * Every column-reference field (filter.where leaf columns, sort.keys[].column,
 * rollup.columns, rollup.aggregations[].column, pivot.{key,value}Column,
 * fold.columns, spread.column, unroll.column, expandTimeseries.{columns,
 * splitBy}) accepts a literal column name, a dotted path, OR a math.js
 * expression. Resolution happens once per action via
 * {@link prepareTableForAction}, immediately before that action runs:
 * expressions are evaluated against the live table as synthetic derives,
 * dotted paths whose root exists on the live table are materialised as
 * flat columns, literal column refs are validated. Synthetic columns
 * (`__expr_<hash>`) are stripped from the final output so they stay
 * invisible to the caller.
 *
 * ## Column-type propagation
 *
 * The input dataset carries a {@link ColumnSchema} per column. Row values are
 * coerced to their declared type before Arquero sees them so downstream
 * filter predicates and aggregations compare values consistently. Columns
 * whose shape is untouched by the pipeline keep their producer-declared
 * metadata (refKind, unit, description); columns created or reshaped by
 * actions are re-inferred from the final Arquero table via
 * {@link inferColumnSchemas}.
 */
export async function transformDataset(
  input: Dataset,
  spec: PipelineSpec,
  ctx?: PrefetchCtx,
): Promise<TransformDatasetSuccess> {
  const data = coerceRowsForSchema(input.rows, input.columns);

  if (data.length === 0) {
    return {
      success: true,
      dataset: createDataset({ rows: [], columns: input.columns, meta: input.meta }),
    };
  }

  const initialWarnings: readonly string[] =
    data.length > MAX_ROWS_WARNING ? [`Processing ${data.length} rows may be slow in browser`] : [];

  type StepState = {
    table: ColumnTable;
    warnings: readonly string[];
    aggregations?: AggregationResult;
    groupedAggregations?: GroupedAggregations;
  };
  const initialState: StepState = {
    table: datasetToTable({ rows: data, columns: input.columns }),
    warnings: initialWarnings,
  };

  // Action loop — fold each action into the running state. Each iteration
  // (a) resolves the action's column-ref sites against the live table via
  // prepareTableForAction (evaluating inline expressions as synthetic
  // derives, materialising dotted paths whose root exists on the table,
  // throwing ColumnNotFoundError against the live column set otherwise),
  // then (b) dispatches to the matching executor in ACTION_EXECUTORS.
  // Failures throw and propagate out of the reduce.
  const finalFlow = await spec.actions.reduce<Promise<StepState>>(async (prevP, action) => {
    const prev = await prevP;
    const prepared = await prepareTableForAction(action, prev.table, ctx);
    const stepResult = await runAction(prepared.action, prepared.table, ctx);
    return {
      table: stepResult.table,
      warnings: [...prev.warnings, ...prepared.warnings, ...(stepResult.warnings ?? [])],
      aggregations: stepResult.aggregations ?? prev.aggregations,
      groupedAggregations: stepResult.groupedAggregations ?? prev.groupedAggregations,
    };
  }, Promise.resolve(initialState));

  // Strip synthetic-derive columns from the final output — implementation
  // detail of the per-action resolver; callers never asked for
  // `__expr_<hash>` columns. Aggregation/derive output names that the
  // user supplied are preserved.
  const allColumns = finalFlow.table.columnNames();
  const visibleColumns = allColumns.filter((c) => !isSyntheticDeriveName(c));
  const finalTable =
    visibleColumns.length < allColumns.length ? finalFlow.table.select(visibleColumns) : finalFlow.table;

  return buildSuccessResult(
    input,
    finalTable,
    [...finalFlow.warnings],
    finalFlow.aggregations,
    finalFlow.groupedAggregations,
  );
}
/**
 * Assemble a {@link TransformDatasetSuccess} from the final Arquero table.
 *
 * Column metadata is preserved from the producer when Arquero's post-pipeline
 * column names match the input schema exactly (no derive / reshape / select /
 * groupBy ran). Otherwise columns are re-inferred from the final rows so the
 * output carries correct types for operator-introduced columns.
 */
function buildSuccessResult(
  input: Dataset,
  table: ColumnTable,
  warnings: string[],
  aggregations: AggregationResult | undefined,
  groupedAggregations: GroupedAggregations | undefined,
): TransformDatasetSuccess {
  const finalRows = table.objects() as Record<string, unknown>[];
  const finalColumnNames = table.columnNames();
  const finalColumns = finalColumnsMatchInput(input.columns, finalColumnNames)
    ? input.columns
    : propagateColumns(input.columns, finalRows, finalColumnNames);

  return {
    success: true,
    dataset: createDataset({
      rows: finalRows,
      columns: finalColumns,
      meta: input.meta,
    }),
    warnings: warnings.length > 0 ? warnings : undefined,
    aggregations,
    groupedAggregations,
  };
}

/** Fast-path check: did Arquero's final column set match the input schema by name and order? */
function finalColumnsMatchInput(inputColumns: readonly ColumnSchema[], finalColumnNames: readonly string[]): boolean {
  if (inputColumns.length !== finalColumnNames.length) {
    return false;
  }
  for (let i = 0; i < inputColumns.length; i += 1) {
    if (inputColumns[i]?.name !== finalColumnNames[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Rebuild a column schema after operators changed the pipeline shape.
 *
 * For each column in the final Arquero table: prefer the producer-declared
 * schema entry (preserving refKind, unit, description) when the name
 * survived unchanged; otherwise fall back to a fresh inference from the
 * final row values.
 */
function propagateColumns(
  priorColumns: readonly ColumnSchema[],
  finalRows: readonly Record<string, unknown>[],
  finalColumnNames: readonly string[],
): ColumnSchema[] {
  const priorByName = new Map(priorColumns.map((c) => [c.name, c]));
  const inferredByName = new Map(inferColumnSchemas(finalRows).map((c) => [c.name, c]));
  return finalColumnNames.map(
    (name) => priorByName.get(name) ?? inferredByName.get(name) ?? { name, type: 'unknown' as const },
  );
}

/**
 * Coerce each row value to its declared column type.
 *
 * Producers that emit string-encoded numbers (e.g. from CDF instance queries
 * that flatten untyped property dictionaries) get their values snapped to
 * JS `number` here, so Arquero stores the correct runtime type and downstream
 * predicates (gt/lt/range/avg/sum) operate as expected. This is the structural
 * fix for the numeric-filter FIXME.
 *
 * Rows are always copied (never mutated in place) so the input Dataset's
 * ReadonlyArray contract is preserved.
 */
function coerceRowsForSchema(
  rows: readonly Readonly<Record<string, unknown>>[],
  columns: readonly ColumnSchema[],
): Record<string, unknown>[] {
  if (columns.length === 0) {
    return rows.map((row) => ({ ...row }));
  }
  const columnTypes = new Map(columns.map((c) => [c.name, c.type]));
  return rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([name, value]) => {
        const type = columnTypes.get(name);
        return [name, type === undefined ? value : coerceValue(value, type)] as const;
      }),
    ),
  );
}

/**
 * Coerce a single value according to its declared {@link DatasetColumnType}.
 *
 * Returns `null` for numeric values that don't parse cleanly so downstream
 * consumers can distinguish "missing" from "malformed". `datetime`, `ref`,
 * and `unknown` pass through: Arquero handles Date/string comparisons
 * natively, and `ref` / `unknown` have no meaningful coercion.
 */
function coerceValue(value: unknown, type: DatasetColumnType): unknown {
  if (value == null) {
    return value;
  }
  // biome-ignore lint/nursery/noUnnecessaryConditions: false positive — exhaustive switch on DatasetColumnType union, not a condition
  switch (type) {
    case 'number': {
      if (typeof value === 'number') {
        return value;
      }
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    case 'string':
      return typeof value === 'string' ? value : String(value);
    case 'boolean':
      return typeof value === 'boolean' ? value : Boolean(value);
    case 'datetime':
    case 'ref':
    case 'unknown':
      return value;
  }
}
